import type { LonLat, Massnahme, Status } from './api/client';

const TZ = 'Europe/Berlin';

export function uhrzeit(iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  });
}

export function datum(iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('de-DE', { dateStyle: 'medium', timeZone: TZ });
}

export function datumZeit(iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: TZ,
  });
}

/** ISO → Wert für <input type="datetime-local"> in Europe/Berlin. */
export function zuLokal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const teile = new Intl.DateTimeFormat('sv-SE', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return teile.replace(' ', 'T');
}

/** Wert aus <input type="datetime-local"> (Europe/Berlin, ohne Zone) → an den Server, der Europe/Berlin annimmt. */
export function ausLokal(wert: string): string | null {
  return wert === '' ? null : `${wert}:00`;
}

export const STATUS_TEXT: Record<Status, string> = {
  rot: 'Rot – klären',
  gelb: 'Gelb – prüfen',
  gruen: 'Grün – in Ordnung',
  polizei: 'Polizei',
  grau: 'Hinweis',
};

/** Kurzzeichen je Status – auch in Graustufen unterscheidbar. */
export const STATUS_ZEICHEN: Record<Status, string> = {
  rot: '!',
  gelb: '?',
  gruen: '✓',
  polizei: 'P',
  grau: '·',
};

export const MASSNAHME_TEXT: Record<Massnahme, string> = {
  vollsperrung: 'Vollsperrung',
  teilsperrung: 'Teilsperrung',
  kreisverkehr: 'Kreisverkehr',
  polizei: 'Polizei',
};

export const ART_TEXT: Record<string, string> = {
  laternenumzug: 'Laternenumzug',
  st_martin: 'St. Martin',
  schuetzenumzug: 'Schützenumzug',
  karneval: 'Karneval',
  sonstiges: 'Sonstiger Umzug',
};

export const MATERIAL_TEXT: Record<string, string> = {
  absperrgitter: 'Absperrgitter',
  warnleuchte: 'Warnleuchte',
  absperrschranke: 'Absperrschranke',
  warnweste: 'Warnweste',
  leuchtstab: 'Leuchtstab',
};

export function material(m: Record<string, number> | undefined | null): string {
  const e = Object.entries(m ?? {}).filter(([, n]) => n > 0);
  if (e.length === 0) return '–';
  return e.map(([art, n]) => `${n}× ${MATERIAL_TEXT[art] ?? art}`).join(', ');
}

// ---------- Geometrie (nur Vorschau im Browser; maßgeblich rechnet das Backend) ----------

function abstandM(a: LonLat, b: LonLat): number {
  const r = 6371008.8;
  const p1 = (a[1] * Math.PI) / 180;
  const p2 = (b[1] * Math.PI) / 180;
  const dp = p2 - p1;
  const dl = ((b[0] - a[0]) * Math.PI) / 180;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

/** Punkt bei Station s (Meter ab Start) entlang der Route; begrenzt auf Anfang/Ende. */
export function punktBei(route: LonLat[], s: number): LonLat {
  if (route.length === 0) return [0, 0];
  if (s <= 0) return route[0]!;
  let rest = s;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1]!;
    const b = route[i]!;
    const d = abstandM(a, b);
    if (rest <= d && d > 0) {
      const t = rest / d;
      return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
    }
    rest -= d;
  }
  return route[route.length - 1]!;
}

/** Teilstück der Route zwischen zwei Stationen. */
export function abschnitt(route: LonLat[], von: number, bis: number): LonLat[] {
  const out: LonLat[] = [punktBei(route, von)];
  let s = 0;
  for (let i = 1; i < route.length; i++) {
    s += abstandM(route[i - 1]!, route[i]!);
    if (s > von && s < bis) out.push(route[i]!);
  }
  out.push(punktBei(route, bis));
  return out;
}

/**
 * Wo ist der Zug zum Zeitpunkt t? Zugspitze bei v·(t − Start), Zugende Zuglänge dahinter.
 * null, wenn der Zug noch nicht gestartet oder schon am Ziel angekommen ist.
 */
export function zugPosition(
  t: number,
  start: number,
  vKmh: number,
  zuglaengeM: number,
  routeLaengeM: number,
): { spitze: number; ende: number } | null {
  const vMs = (vKmh * 1000) / 3600;
  const spitze = ((t - start) / 1000) * vMs;
  const ende = spitze - zuglaengeM;
  if (spitze < 0 || ende > routeLaengeM) return null;
  return { spitze: Math.min(spitze, routeLaengeM), ende: Math.max(ende, 0) };
}
